import React, { useState, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTriangleExclamation, faSpinner, faEnvelope, faSms } from '@fortawesome/free-solid-svg-icons';
import {
  todayCalendarDate,
  isValidCalendarDate,
  formatCalendarDateDisplay,
  calendarDateFromInstant,
  calendarDateMonthsBefore,
} from '@medprompt/shared';
import { MeasurementTypeEnum } from '../enums/measurementTypeEnum';
import { TaskTypeEnum } from '../enums/taskTypeEnum';
import CustomDatePicker from '@medprompt/shared/src/common/CustomDatePicker';
import '@medprompt/shared/src/common/modals.css';
import { Significance } from '../enums/significanceEnum';
import { FREQUENT_CODES_PHRASE } from './tabs/codeSuggest';
import { FeatureFlags } from '../FeatureFlags';
import {
  BLOOD_PRESSURE_DISPLAY_NAME,
  bundleBloodPressureReadings,
  formatBloodPressureReading,
  getDisplayRowId,
  isBloodPressurePair,
} from './measurements/bundleBloodPressureReadings';
import './FileAwayModal.css';

const FileAwayModal = ({
  doc,
  activeUsers,
  teams = [],
  comms = [],
  onConfirm,
  onCancel,
  isProcessing,
  entityType = 'document',
  shouldBundleBloodPressureReadings = false,
}) => {
  const isConsultation = entityType === 'consultation';
  const existingPatientCodes = isConsultation ? doc.existingCodes : doc.existingPatientCodes;
  const newCodeSuggests = isConsultation ? doc.newCodes : doc.newCodeSuggests;
  const frequentCodes = isConsultation ? undefined : doc.frequentCodes;
  const summaryText = isConsultation ? (doc.notes || doc.summary) : doc.briefSummary;
  const comments = isConsultation ? null : doc.comments;
  const entityLabel = isConsultation ? 'consultation' : 'document';

  const isValidDateFormat = isValidCalendarDate;

  const formatDateForDisplay = formatCalendarDateDisplay;

  const entityDate = useMemo(() => {
    if (isConsultation) {
      const consultationDate = calendarDateFromInstant(doc?.timestamp);
      return isValidDateFormat(consultationDate) ? consultationDate : null;
    }
    const docDate = doc?.documentDetails?.date;
    return isValidDateFormat(docDate) ? docDate : null;
  }, [isConsultation, doc?.timestamp, doc?.documentDetails?.date]);

  // Default to document/consultation date if available and valid, otherwise today
  const [fileAwayDate, setFileAwayDate] = useState(() => entityDate || todayCalendarDate());

  const fileAwayDateNotice = useMemo(() => {
    if (!isValidCalendarDate(fileAwayDate)) return null;

    const today = todayCalendarDate();
    let reason = null;
    if (fileAwayDate > today) {
      reason = 'in the future';
    } else {
      const threeMonthsAgo = calendarDateMonthsBefore(today, 3);
      if (threeMonthsAgo && fileAwayDate < threeMonthsAgo) {
        reason = 'more than 3 months ago';
      }
    }

    if (!reason) return null;

    return {
      warning: `This file-away date is ${reason}. Filing away stays blocked until you confirm this date is correct.`,
      confirmLabel: 'I have checked this date and it is correct to file',
    };
  }, [fileAwayDate]);

  const [acknowledgedFileAwayDate, setAcknowledgedFileAwayDate] = useState(null);
  const unusualDateAcknowledged = acknowledgedFileAwayDate === fileAwayDate;

  const dateButtonInfo = useMemo(() => {
    if (!entityDate) return null;

    const today = todayCalendarDate();

    if (fileAwayDate === entityDate) {
      return {
        label: 'Use Today',
        targetDate: today,
        displayDate: formatDateForDisplay(today)
      };
    }

    return {
      label: isConsultation ? 'Use Consultation Date' : 'Use Document Date',
      targetDate: entityDate,
      displayDate: formatDateForDisplay(entityDate)
    };
  }, [entityDate, fileAwayDate, isConsultation]);

  const handleToggleDateButton = useCallback(() => {
    if (dateButtonInfo) {
      setFileAwayDate(dateButtonInfo.targetDate);
    }
  }, [dateButtonInfo]);
  
  // State for expanded comms (track which comms are expanded)
  const [expandedComms, setExpandedComms] = useState(new Set());
  
  // State for include comms - checked by default if comms exist
  const [shouldIncludeComms, setShouldIncludeComms] = useState(true);

  // State for include outcome tasks - checked by default when tasks are selected
  const [shouldIncludeTasks, setShouldIncludeTasks] = useState(true);
  
  // State for selected comment IDs - unchecked by default
  const [selectedComments, setSelectedComments] = useState(new Set());

  // Multi-patient uploads stay blocked until the user confirms this file-away is safe.
  const requiresMultiplePatientsConfirmation = !isConsultation && doc?.isMultiplePatients === true;
  const [multiplePatientsAcknowledged, setMultiplePatientsAcknowledged] = useState(false);
  
  // Memoized helper to get selected existing codes
  const selectedExistingCodes = useMemo(() => {
    const selected = [];
    
    if (existingPatientCodes?.activeCodes) {
      existingPatientCodes.activeCodes.forEach(codeWrapper => {
        if (codeWrapper.isSelected) {
          selected.push({
            ...codeWrapper.code,
            category: 'Active',
            significance: codeWrapper.significance ?? Significance.MINOR.value,
          });
        }
      });
    }
    
    if (existingPatientCodes?.significantPastCodes) {
      existingPatientCodes.significantPastCodes.forEach(codeWrapper => {
        if (codeWrapper.isSelected) {
          selected.push({
            ...codeWrapper.code,
            category: 'Significant Past',
            significance: codeWrapper.significance ?? Significance.MINOR.value,
          });
        }
      });
    }
    
    return selected;
  }, [existingPatientCodes]);

  // Memoized helper to get selected new codes
  const selectedNewCodes = useMemo(() => {
    const selected = [];
    
    if (newCodeSuggests) {
      newCodeSuggests.forEach(codeSuggest => {
        if (codeSuggest.suggestions) {
          codeSuggest.suggestions.forEach(suggestion => {
            if (suggestion.isSelected) {
              selected.push({
                ...suggestion.record,
                phrase: codeSuggest.phrase,
                significance: suggestion.significance ?? Significance.MAJOR.value,
              });
            }
          });
        }
      });
    }

    if (frequentCodes) {
      frequentCodes.forEach((code) => {
        if (code.isSelected) {
          selected.push({
            ...code.record,
            phrase: FREQUENT_CODES_PHRASE,
            significance: code.significance ?? Significance.MAJOR.value,
          });
        }
      });
    }
    
    return selected;
  }, [newCodeSuggests, frequentCodes]);

  // Memoized helper to get selected measurements
  const selectedMeasurements = useMemo(() => {
    if (!doc.measurements) return [];
    return doc.measurements.filter(measurement => measurement.isSelected);
  }, [doc.measurements]);

  const selectedMeasurementRows = useMemo(
    () => bundleBloodPressureReadings(selectedMeasurements, shouldBundleBloodPressureReadings),
    [selectedMeasurements, shouldBundleBloodPressureReadings]
  );

  // Memoized helper to get selected outcome tasks
  const selectedOutcomeTasks = useMemo(() => {
    if (!doc.outcomeTasks) return [];
    return doc.outcomeTasks.filter(task => task.isSelected && task.taskType !== TaskTypeEnum.PATIENT_NOTE.value);
  }, [doc.outcomeTasks]);

  // Helper function to get assignee name
  const getAssigneeName = useCallback((assigneeId) => {
    if (!assigneeId) return null;
    
    // Check users first
    const user = activeUsers?.find((user) => user.userId === assigneeId);
    if (user) return user.name;
    
    // Check teams
    const team = teams?.find((team) => team.teamId === assigneeId);
    if (team) return team.teamName;
    
    // Fallback to unknown
    return `Unknown`;
  }, [activeUsers, teams]);

  // Helper function to format timestamp
  const formatTimestamp = useCallback((timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  }, []);

  // Helper function to get communication types info
  const getCommTypesInfo = useCallback((typeToIdentifier) => {
    if (!typeToIdentifier) return [];
    
    const types = [];
    if (typeToIdentifier.TEXT) {
      types.push({
        icon: faSms,
        label: `SMS to ${typeToIdentifier.TEXT}`
      });
    }
    if (typeToIdentifier.EMAIL) {
      types.push({
        icon: faEnvelope,
        label: `Email to ${typeToIdentifier.EMAIL}`
      });
    }
    return types;
  }, []);

  // Helper function to toggle comm expansion
  const toggleCommExpansion = useCallback((commId) => {
    setExpandedComms(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commId)) {
        newSet.delete(commId);
      } else {
        newSet.add(commId);
      }
      return newSet;
    });
  }, []);

  // Helper function to toggle comment selection
  const toggleCommentSelection = useCallback((commentId) => {
    setSelectedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  }, []);

  // Helper function to toggle all comments
  const toggleAllComments = useCallback(() => {
    if (selectedComments.size === comments?.length) {
      // Deselect all
      setSelectedComments(new Set());
    } else {
      // Select all
      setSelectedComments(new Set(comments.map(c => c.commentId)));
    }
  }, [selectedComments.size, comments]);

  // Helper function to truncate content to 2 lines
  const getTruncatedContent = (content) => {
    const lines = content.split('\n');
    if (lines.length <= 2) {
      return { truncated: content, needsExpansion: false };
    }
    return {
      truncated: lines.slice(0, 2).join('\n'),
      needsExpansion: true
    };
  };

  // Check if items exist but none selected
  const hasExistingCodes = existingPatientCodes?.activeCodes?.length > 0 || existingPatientCodes?.significantPastCodes?.length > 0;
  const hasNewCodes = newCodeSuggests?.length > 0 || frequentCodes?.length > 0;
  const hasMeasurements = doc.measurements?.length > 0;
  const hasOutcomeTasks = doc.outcomeTasks?.length > 0;

  const visibilityDetails = isConsultation ? doc?.consultationDetails : doc?.documentDetails;
  const isPatientFacingVisible = !(visibilityDetails?.isHiddenFromPatient ?? false);

  // Check if there are any warning conditions
  const hasWarnings = useMemo(() => {
    const noSelectedExistingCodes = hasExistingCodes && selectedExistingCodes.length === 0;
    const noSelectedNewCodes = hasNewCodes && selectedNewCodes.length === 0;
    const noSelectedMeasurements = hasMeasurements && selectedMeasurements.length === 0;
    const noSelectedOutcomeTasks = hasOutcomeTasks && selectedOutcomeTasks.length === 0;
    const unassignedSelectedTasks = selectedOutcomeTasks.some(task => !getAssigneeName(task.assigneeId));
    
    // Check if there are patient note tasks but no comms sent
    const hasPatientNoteTasks = selectedOutcomeTasks.some(task => task.taskType === TaskTypeEnum.PATIENT_NOTE.value);
    const noCommsSent = (!comms || comms.length === 0) && hasPatientNoteTasks;
    
    return noSelectedExistingCodes || noSelectedNewCodes || noSelectedMeasurements || 
           noSelectedOutcomeTasks || unassignedSelectedTasks || noCommsSent;
  }, [hasExistingCodes, hasNewCodes, hasMeasurements, hasOutcomeTasks, selectedExistingCodes, selectedNewCodes, selectedMeasurements, selectedOutcomeTasks, comms, getAssigneeName]);

  // Check if document is ready to file away - mirrors backend logic
  const tasksReadyToFileAway = useMemo(() => {
    // Apply the same predicate logic as the backend: all selected tasks must have an assignee
    return selectedOutcomeTasks.every(task => getAssigneeName(task.assigneeId) !== null);
  }, [selectedOutcomeTasks, getAssigneeName]);

  const requiresUnusualDateConfirmation = fileAwayDateNotice != null;

  const isReadyToFileAway = tasksReadyToFileAway
    && (!requiresMultiplePatientsConfirmation || multiplePatientsAcknowledged)
    && (!requiresUnusualDateConfirmation || unusualDateAcknowledged);

  const fileAwayBlockReason = useMemo(() => {
    const blockers = [];
    if (requiresMultiplePatientsConfirmation && !multiplePatientsAcknowledged) {
      blockers.push('confirm this upload is safe to file');
    }
    if (requiresUnusualDateConfirmation && !unusualDateAcknowledged) {
      blockers.push('confirm this file-away date is correct');
    }
    if (!tasksReadyToFileAway) {
      blockers.push('assign all selected tasks');
    }
    if (blockers.length === 0) {
      return null;
    }
    return `Cannot file away - ${blockers.join(' and ')}`;
  }, [
    requiresMultiplePatientsConfirmation,
    multiplePatientsAcknowledged,
    requiresUnusualDateConfirmation,
    unusualDateAcknowledged,
    tasksReadyToFileAway,
  ]);

  const significanceClassForCode = useCallback((significanceValue) => {
    const sig = significanceValue ?? Significance.MINOR.value;
    if (sig === Significance.MAJOR.value) return 'selected-major-code-item';
    if (sig === Significance.ADMINISTRATIVE.value) return 'selected-admin-code-item';
    return 'selected-minor-code-item';
  }, []);

  // Render code item (new codes omit phrase — too crowded in file-away preview)
  const renderCodeItem = (code) => (
    <div
      key={`${code.code}-${code.category || ''}-${code.phrase || ''}`}
      className={`selected-code-item ${significanceClassForCode(code.significance)}`}
    >
      <span className="code-id">({code.code})</span>
      <span className="code-term">{code.terms?.[0] || code.term || ''}</span>
      {code.category && <span className="code-category">{code.category}</span>}
    </div>
  );

  // Render measurement item
  const renderMeasurementItem = (measurement) => {
    const typeLabel = MeasurementTypeEnum[measurement.type]?.displayName || measurement.type;
    const detail = measurement.valueType === 'NONE'
      ? (measurement.code?.terms?.[0] || measurement.code?.code || '')
      : `${measurement.value} ${measurement.unit || ''}`.trim();

    return (
      <div key={measurement.measurementId || measurement.type} className="selected-measurement-item">
        <span className="code-id">{typeLabel}</span>
        {detail && <span className="code-term">{detail}</span>}
      </div>
    );
  };

  const renderMeasurementRow = (row) => {
    if (isBloodPressurePair(row)) {
      return (
        <div key={getDisplayRowId(row)} className="selected-measurement-item">
          <span className="code-id">{BLOOD_PRESSURE_DISPLAY_NAME}</span>
          <span className="code-term">
            {formatBloodPressureReading(row.systolic.measurement, row.diastolic.measurement)}
          </span>
        </div>
      );
    }
    return renderMeasurementItem(row.measurement);
  };

  // Render task item
  const renderTaskItem = (task) => {
    const assigneeName = getAssigneeName(task.assigneeId);
    const taskType = TaskTypeEnum[task.taskType]?.description || task.taskType;
    
    return (
      <div key={task.taskId} className="selected-task-item">
        <div className="task-main-content">
          <span className="code-term">{task.text}</span>
          <span className="code-phrase">{taskType}</span>
          {assigneeName ? (
            <span className="code-phrase">{assigneeName}</span>
          ) : (
            <div className="task-unassigned-warning">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              No assignee
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render comm item
  const renderCommItem = (comm) => {
    const commTypes = getCommTypesInfo(comm.typeToIdentifier);
    const isExpanded = expandedComms.has(comm.commId);
    const { truncated, needsExpansion } = getTruncatedContent(comm.content);
    const contentToShow = isExpanded ? comm.content : truncated;
    
    return (
      <div key={comm.commId} className="selected-comm-item">
        <div className="comm-header">
          <div className="comm-types">
            {commTypes.length > 0 ? (
              commTypes.map((type, index) => (
                <div key={index} className="comm-type">
                  <FontAwesomeIcon icon={type.icon} />
                  <span>{type.label}</span>
                </div>
              ))
            ) : (
              <div className="comm-type">
                <span>Unknown</span>
              </div>
            )}
          </div>
          <div className="comm-timestamp">{formatTimestamp(comm.timestamp)}</div>
        </div>
        <div className="comm-content">
          <pre className="comm-content-text">{contentToShow}</pre>
          {needsExpansion && (
            <button 
              className="comm-expand-button" 
              onClick={() => toggleCommExpansion(comm.commId)}
            >
              {isExpanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      </div>
    );
  };

  // Render comment item
  const renderCommentItem = (comment, index) => {
    const commentUser = comment.userId === 'SYSTEM'
      ? { name: 'MedPrompt App' }
      : activeUsers?.find(u => u.userId === comment.userId);
    const commentDate = new Date(comment.timestamp);
    const isExpanded = expandedComms.has(`comment-${comment.commentId}`);
    const { truncated, needsExpansion } = getTruncatedContent(comment.comment);
    const contentToShow = isExpanded ? comment.comment : truncated;
    
    return (
      <div key={comment.commentId || index} className="selected-comment-item">
        <div className="comment-details">
          <div className="comment-header">
            <div className="comment-header-left">
              <input
                type="checkbox"
                checked={selectedComments.has(comment.commentId)}
                onChange={() => toggleCommentSelection(comment.commentId)}
                className="comment-checkbox"
              />
              <span className="comment-author">{commentUser?.name || 'Unknown'}</span>
            </div>
            <span className="comment-timestamp">{commentDate.toLocaleString()}</span>
          </div>
          <div className="comment-text-content">
            <pre className="comment-text">{contentToShow}</pre>
            {needsExpansion && (
              <button 
                className="comment-expand-button" 
                onClick={() => toggleCommExpansion(`comment-${comment.commentId}`)}
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render section with warning or content
  const renderSection = (title, items, hasItems, itemRenderer) => (
    <div className="fileaway-section">
      <h3>{title}</h3>
      {!hasItems ? (
        <div className="fileaway-none">None</div>
      ) : items.length === 0 ? (
        <div className="fileaway-warning">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          None selected
        </div>
      ) : (
        <div className="fileaway-items">
          {items.map(itemRenderer)}
        </div>
      )}
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-container fileaway-modal modal-animated" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>File Away {isConsultation ? 'Consultation' : 'Document'}</h2>
          <button className="close-button" onClick={onCancel} disabled={isProcessing}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="modal-content">
          <p className="fileaway-intro">
            You are about to file away this {entityLabel}. Please review the selected content below:
          </p>

          {(requiresMultiplePatientsConfirmation || fileAwayDateNotice) && (
            <div className="fileaway-alert-row">
              {requiresMultiplePatientsConfirmation && (
                <div className="fileaway-multiple-patients">
                  <div className="fileaway-multiple-patients-warning">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    <span>This upload may contain documents for more than one patient. Filing away stays blocked until you confirm it is safe.</span>
                  </div>
                  <label className="fileaway-checkbox-label fileaway-multiple-patients-confirm">
                    <input
                      type="checkbox"
                      checked={multiplePatientsAcknowledged}
                      onChange={(e) => setMultiplePatientsAcknowledged(e.target.checked)}
                      disabled={isProcessing}
                    />
                    <span>I have checked this document and it is safe to file to the matched patient</span>
                  </label>
                </div>
              )}
              {fileAwayDateNotice && (
                <div className="fileaway-multiple-patients">
                  <div className="fileaway-multiple-patients-warning">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    <span>{fileAwayDateNotice.warning}</span>
                  </div>
                  <label className="fileaway-checkbox-label fileaway-multiple-patients-confirm">
                    <input
                      type="checkbox"
                      checked={unusualDateAcknowledged}
                      onChange={(e) => setAcknowledgedFileAwayDate(e.target.checked ? fileAwayDate : null)}
                      disabled={isProcessing}
                    />
                    <span>{fileAwayDateNotice.confirmLabel}</span>
                  </label>
                </div>
              )}
            </div>
          )}
          
          {/* Warning Section */}
          {hasWarnings && (
            <div className="fileaway-document-warning">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              The {entityLabel} may have issues below - please check before filing away.
            </div>
          )}
          
          {/* Three Column Layout */}
          <div className="fileaway-columns">
            {/* Column 1: Summary, Pre-Existing Codes, New Codes */}
            <div className="fileaway-column-wrapper">
              <div className="fileaway-column-content">
                {FeatureFlags.PATIENT_FACING_VISIBILITY_TOGGLE && (
                  <label className="fileaway-checkbox-label fileaway-patient-visibility">
                    <input
                      type="checkbox"
                      checked={isPatientFacingVisible}
                      disabled
                      readOnly
                    />
                    <span>Display on patient's online record</span>
                  </label>
                )}

                {/* Brief Summary / Notes */}
                <div className="fileaway-section">
                  <h3>{isConsultation ? 'Notes' : 'Summary'}</h3>
                  <div className={`fileaway-summary${isConsultation ? ' fileaway-summary-notes' : ''}`}>
                    {summaryText || (isConsultation ? 'No notes available' : 'No summary available')}
                  </div>
                </div>

                {/* Selected Content Sections */}
                {renderSection(
                  'Pre-Existing Codes',
                  selectedExistingCodes,
                  hasExistingCodes,
                  renderCodeItem
                )}

                {renderSection(
                  'New Codes',
                  selectedNewCodes,
                  hasNewCodes,
                  renderCodeItem
                )}
              </div>
            </div>

            {/* Column 2: Measurements, Outcome Tasks */}
            <div className="fileaway-column-wrapper">
              <div className="fileaway-column-content">
                {renderSection(
                  'Measurements',
                  selectedMeasurementRows,
                  hasMeasurements,
                  renderMeasurementRow
                )}

                <div className="fileaway-section">
                  <h3>Outcome Tasks</h3>
                  {!hasOutcomeTasks ? (
                    <div className="fileaway-none">None</div>
                  ) : selectedOutcomeTasks.length === 0 ? (
                    <div className="fileaway-warning">
                      <FontAwesomeIcon icon={faTriangleExclamation} />
                      None selected
                    </div>
                  ) : (
                    <div className="fileaway-items">
                      {selectedOutcomeTasks.map(renderTaskItem)}
                    </div>
                  )}

                  <label className="fileaway-checkbox-label">
                    <input
                      type="checkbox"
                      checked={shouldIncludeTasks}
                      onChange={(e) => setShouldIncludeTasks(e.target.checked)}
                      disabled={selectedOutcomeTasks.length === 0}
                    />
                    <span>Include outcome tasks</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Column 3: Clinical department, Patient Communications, Comments */}
            <div className="fileaway-column-wrapper">
              <div className="fileaway-column-content">
                {/* Document Type */}
                {!isConsultation && (() => {
                  const activeField = doc.documentDetails?.documentTypeField ?? 'CLINICAL_DEPARTMENT';
                  const activeCode = activeField === 'LETTER_TYPE'
                    ? doc.documentDetails?.letterTypeCode
                    : doc.documentDetails?.clinicalDepartment;
                  if (!activeCode) return null;
                  const isCustom = String(activeCode.code ?? '').startsWith('CUSTOM');
                  return (
                    <div className="fileaway-section">
                      <h3>Document Type</h3>
                      <div className="fileaway-clinical-dept selected-code-item selected-admin-code-item">
                        <span className="code-id">{isCustom ? 'custom' : `(${activeCode.code})`}</span>
                        <span className="code-term">
                          {activeCode.terms?.[0] ?? ''}
                        </span>
                      </div>
                    </div>
                  );
                })()}

                {/* Patient Communications */}
                <div className="fileaway-section">
                  <h3>Patient Communications</h3>
                  {comms.length === 0 ? (
                    <>
                      {selectedOutcomeTasks.some(task => task.taskType === TaskTypeEnum.PATIENT_NOTE.value) ? (
                        <div className="fileaway-warning">
                          <FontAwesomeIcon icon={faTriangleExclamation} />
                          No communications sent (patient note task exists)
                        </div>
                      ) : (
                        <div className="fileaway-none">None sent</div>
                      )}
                    </>
                  ) : (
                    <div className="fileaway-items">
                      {comms.map(renderCommItem)}
                    </div>
                  )}

                  <label className="fileaway-checkbox-label">
                    <input
                      type="checkbox"
                      checked={shouldIncludeComms}
                      onChange={(e) => setShouldIncludeComms(e.target.checked)}
                      disabled={comms.length === 0}
                    />
                    <span>Include patient communications</span>
                  </label>
                </div>

                {/* Document Comments Selection */}
                {!isConsultation && (
                <div className="fileaway-section">
                  <div className="fileaway-section-header">
                    <h3>Comments</h3>
                    {comments && comments.length > 0 && (
                      <button 
                        className="select-all-button" 
                        onClick={toggleAllComments}
                      >
                        {selectedComments.size === comments.length ? 'Deselect All' : 'Select All'}
                      </button>
                    )}
                  </div>
                  {!comments || comments.length === 0 ? (
                    <div className="fileaway-none">No comments</div>
                  ) : (
                    <div className="fileaway-comments-list">
                      {comments.map((comment, index) => renderCommentItem(comment, index))}
                    </div>
                  )}
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="modal-actions">
          <div className="modal-actions-vertical">
            <div className="modal-actions-horizontal">
              <div className="fileaway-date-section">
                <CustomDatePicker
                  value={fileAwayDate}
                  onChange={setFileAwayDate}
                  label="File Away Date"
                  max={todayCalendarDate()} // Can't file away in the future
                  opensAbove={true}
                />
                {dateButtonInfo && (
                  <button 
                    className="use-doc-date-button"
                    onClick={handleToggleDateButton}
                    disabled={isProcessing}
                  >
                    {dateButtonInfo.label}: {dateButtonInfo.displayDate}
                  </button>
                )}
              </div>
              <div className="fileaway-buttons-group">
                <button 
                  className="cancel-button" 
                  onClick={onCancel}
                  disabled={isProcessing}
                >
                  Cancel
                </button>
                <div className={`file-away-button-wrapper ${!isReadyToFileAway && !isProcessing ? 'file-away-disabled-patient' : ''}`}>
                  <button 
                    className="header-button header-button-primary" 
                    onClick={() => onConfirm(fileAwayDate, shouldIncludeTasks, shouldIncludeComms, Array.from(selectedComments))}
                    disabled={isProcessing || !isReadyToFileAway}
                  >
                    {isProcessing ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin /> Filing Away...
                      </>
                    ) : (
                      'File Away'
                    )}
                  </button>
                  {fileAwayBlockReason && !isProcessing && (
                    <div className="file-away-tooltip">{fileAwayBlockReason}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(FileAwayModal);
